import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import { min, cloneDeep, isNil } from "lodash";
import {
  SUCCESS_FETCH_LOOKUP_TABLES,
  SUCCESS_LOCK_DEPOT,
  SUCCESS_UNLOCK_DEPOT,
  SUCCESS_FETCH_POSTCODE_ALLOCATIONS,
  ERROR_FETCH_DEPOT_CONFIG,
  ERROR_FETCH_LOOKUP_TABLES,
  ERROR_FETCH_POSTCODE_ALLOCATIONS,
  ERROR_REMOVE_LAST_POSTCODE,
  ERROR_REMOVE_IN_USE_POSTCODE,
} from "../../consts/messages";
import {
  GET_DEPOT_POSTCODES,
  GET_LOOK_UP_TABLES,
  SAVE_LOOK_UP_TABLES,
  LOCK_DEPOT,
  UNLOCK_DEPOT,
  IS_POSTCODE_IN_USE,
  GET_DEPOT_CONFIG,
} from "../../consts/apis";
import { MAX_SECTORS, MAX_POSTCODES } from "../../consts/map";
import { DEPOT_MAP_CHANGED } from "../../consts/errorCodes";

import { addNotification, setPostcodeInUseModalContent } from "../ownApp";
import { resetArtificialFeatures } from "../map";
import api from "../../services/api";
import { getBbox } from "../../utils/map";
import {
  generateLookupTablesExtras,
  generateArtificialTables,
} from "../../utils/lookUpTables";
import { setFitBounds } from "../map/thunkActions";
import {
  activeSaebiVersionSelector,
  depotChecksumSelector,
  artificialPostcodeInEditSelector,
  artificialPostcodeInEditCopySelector,
  lookupTableSelector,
  fullArtificialTablesSelector,
  depotSessionChecksumSelector,
  partialPostcodesVersionsSelector,
} from "./selectors";
import { Postcode } from "../../utils/postcode";
import { getSelectedDepot } from "../ownApp/selectors";
import {
  getDepotSavedChecksum,
  saveDepotChecksum,
  deleteDepotChecksum,
} from "../../utils/locks";
import { getUserMetadata } from "../ownUser/selectors";

export const resetDepot = createAsyncThunk("depot/resetDepot", async () => {
  // reset depot values
  return {};
});

export const setDepotChecksum = createAction("depot/setDepotChecksum");

export const lockDepot = createAsyncThunk(
  "depot/lockDepot",
  async (params, { getState, dispatch }) => {
    let depotCode;
    try {
      const state = getState();
      depotCode = getSelectedDepot(state);
      const sessionChecksum = depotSessionChecksumSelector(state);
      const checksum = depotChecksumSelector(state);

      const {
        data: { lockedByUserCode, lockedByUserName, lockedDatetime },
      } = await api.put(LOCK_DEPOT(depotCode), null, {
        sessionChecksum,
        checksum,
      });
      saveDepotChecksum(depotCode, checksum);
      dispatch(
        addNotification({
          message: SUCCESS_LOCK_DEPOT,
          options: { variant: "success" },
        })
      );
      return {
        lockedByUserCode,
        lockedByUserName,
        lockedDatetime,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      if (error.statusCode === DEPOT_MAP_CHANGED)
        dispatch(fetchLookUpTables(depotCode));
      throw error;
    }
  }
);

export const unlockDepot = createAsyncThunk(
  "depot/unlockDepot",
  async (forceUnlock = false, { getState, dispatch }) => {
    let depotCode;
    try {
      const state = getState();
      depotCode = getSelectedDepot(state);
      const sessionChecksum = depotSessionChecksumSelector(state);
      const checksum = depotChecksumSelector(state);

      await api.put(UNLOCK_DEPOT(depotCode), null, {
        sessionChecksum,
        checksum,
        forceUnlock,
      });
      deleteDepotChecksum(depotCode);
      dispatch(
        addNotification({
          message: SUCCESS_UNLOCK_DEPOT,
          options: { variant: "success" },
        })
      );
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      if (error.statusCode === DEPOT_MAP_CHANGED)
        setTimeout(() => dispatch(fetchLookUpTables(depotCode)), 0);
      throw error;
    }
  }
);

export const setActiveSaebiVersion = createAsyncThunk(
  "depot/setActiveSaebiVersion",
  async (newSaebiVersion, { getState }) => {
    const state = getState();
    const partialPostcodesVersions = partialPostcodesVersionsSelector(state);
    const { partialPostcodes } = partialPostcodesVersions.find(
      ({ saebiVersion }) => saebiVersion === newSaebiVersion
    );
    return {
      activeSaebiVersion: newSaebiVersion,
      partialPostcodes,
    };
  }
);

export const fetchDepotPartialCodes = createAsyncThunk(
  "depot/getPartialPostcodes",
  async (depotCode, { rejectWithValue, dispatch }) => {
    try {
      const {
        data: { partialPostcodesVersions },
      } = await api.get(GET_DEPOT_POSTCODES(depotCode));
      dispatch(
        addNotification({
          message: SUCCESS_FETCH_POSTCODE_ALLOCATIONS,
          options: { variant: "success" },
        })
      );
      const { saebiVersion, partialPostcodes } = partialPostcodesVersions.find(
        ({ effectiveDate }) => !effectiveDate
      );
      return {
        partialPostcodesVersions,
        activeSaebiVersion: saebiVersion,
        partialPostcodes,
      };
    } catch (e) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_POSTCODE_ALLOCATIONS,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(e.message);
    }
  }
);

export const fetchDepotConfig = createAsyncThunk(
  "depot/fetchDepotConfig",
  async (depotCode, { rejectWithValue, dispatch }) => {
    try {
      const {
        data: { robotPlanningEnabled },
      } = await api.get(GET_DEPOT_CONFIG(depotCode));
      return {
        robotPlanningEnabled,
      };
    } catch (e) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_DEPOT_CONFIG,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(e.message);
    }
  }
);

export const fetchLookUpTables = createAsyncThunk(
  "depot/getLookUpTables",
  async (depotCode, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      const userMetadata = getUserMetadata(state);
      const activeSaebiVersion = activeSaebiVersionSelector(state);

      const {
        data: {
          lookupTables,
          sessionChecksum,
          checksum,
          lockedByUserCode,
          lockedByUserName,
          lockedDatetime,
        },
      } = await api.get(GET_LOOK_UP_TABLES(depotCode, activeSaebiVersion));

      const storedChecksum = getDepotSavedChecksum(depotCode);
      const lockedByYou = lockedByUserCode === userMetadata.user.user_id;
      const lockedInDifferentBrowser =
        storedChecksum !== checksum && lockedByYou;

      if (!lockedByYou && storedChecksum) deleteDepotChecksum(depotCode);

      dispatch(
        setFitBounds({
          bbox: getBbox({
            postcodes: Object.keys(lookupTables.SECTOR),
            lookupTable: lookupTables.SECTOR,
          }),
        })
      );

      generateLookupTablesExtras(lookupTables);
      const artificialTables = generateArtificialTables(lookupTables);
      dispatch(resetArtificialFeatures(artificialTables.visible));

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_LOOKUP_TABLES,
          options: { variant: "success" },
        })
      );
      return {
        sessionChecksum,
        checksum: lockedInDifferentBrowser ? storedChecksum : checksum,
        lockedByUserCode,
        lockedByUserName,
        lockedDatetime,
        lockedByYou,
        lockedInDifferentBrowser,
        lookupTables,
        artificialTables,
        maxSectors: min([
          MAX_SECTORS,
          Object.keys(lookupTables.SECTOR).length * 0.9,
        ]),
        maxPostcodes: min([
          MAX_POSTCODES,
          Object.keys(lookupTables.POSTCODE).length * 0.9,
        ]),
      };
    } catch (e) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_LOOKUP_TABLES,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(e.message);
    }
  }
);

export const saveLookUpTables = createAsyncThunk(
  "depot/saveLookUpTables",
  async (params, { dispatch, getState }) => {
    let depotCode;
    try {
      const state = getState();
      depotCode = getSelectedDepot(state);
      const artificialTables = fullArtificialTablesSelector(state);
      const sessionChecksum = depotSessionChecksumSelector(state);
      const checksum = depotChecksumSelector(state);

      const {
        data: { checksum: newChecksum },
      } = await api.put(SAVE_LOOK_UP_TABLES(depotCode), null, {
        sessionChecksum,
        checksum,
        artificialTables,
      });

      saveDepotChecksum(depotCode, newChecksum);
      return { checksum: newChecksum };
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      if (error.statusCode === DEPOT_MAP_CHANGED)
        dispatch(fetchLookUpTables(depotCode));
      throw error;
    }
  }
);

export const editArtificialPostcode = createAsyncThunk(
  "depot/editArtificialPostcode",
  async ({ lngLat }, { getState, dispatch }) => {
    const state = getState();
    const artificialPostcodeEdit = artificialPostcodeInEditSelector(state);
    const lookupTables = lookupTableSelector(state);
    const { postcode, level } = artificialPostcodeEdit;
    const newArtificialPostcodeEdit = { ...artificialPostcodeEdit };

    const newLookupTables = {
      ...lookupTables,
      [level]: cloneDeep(lookupTables[level]),
    };
    const [lng, lat] = lngLat.toArray();
    newLookupTables[level][postcode].center = [lng, lat];
    newArtificialPostcodeEdit.lng = lng;
    newArtificialPostcodeEdit.lat = lat;

    const artificialTables = generateArtificialTables(newLookupTables);
    dispatch(resetArtificialFeatures(artificialTables.visible));

    return {
      artificialPostcodeEdit: newArtificialPostcodeEdit,
      lookupTables: newLookupTables,
      artificialTables,
    };
  }
);

export const clearArtificialPostcodeLocation = createAsyncThunk(
  "depot/clearArtificialPostcodeLocation",
  async (params, { getState, dispatch }) => {
    const state = getState();
    const artificialPostcodeEdit = artificialPostcodeInEditSelector(state);
    const lookupTables = lookupTableSelector(state);
    const { postcode, level } = artificialPostcodeEdit;
    const newArtificialPostcodeEdit = { ...artificialPostcodeEdit };

    const newLookupTables = {
      ...lookupTables,
      [level]: cloneDeep(lookupTables[level]),
    };
    newLookupTables[level][postcode].center = null;
    newArtificialPostcodeEdit.lat = null;
    newArtificialPostcodeEdit.lng = null;

    const artificialTables = generateArtificialTables(newLookupTables);
    dispatch(resetArtificialFeatures(artificialTables.visible));

    return {
      artificialPostcodeEdit: newArtificialPostcodeEdit,
      lookupTables: newLookupTables,
      artificialTables,
    };
  }
);

export const addArtificialPostcode = createAsyncThunk(
  "depot/addArtificialPostcode",
  async ({ sector, postcodeSuffix }, { getState, dispatch }) => {
    const postcode = `${sector}${postcodeSuffix}`;

    const state = getState();
    const lookupTables = lookupTableSelector(state);

    const newLookupTables = {
      ...lookupTables,
      SECTOR: {
        ...lookupTables.SECTOR,
        [sector]: {
          ...lookupTables.SECTOR[sector],
          children: lookupTables.SECTOR[sector].children + 1,
        },
      },
      POSTCODE: {
        ...lookupTables.POSTCODE,
        [postcode]: {
          postcode,
          id: 0,
          bbox: null,
          center: null,
          area: 0,
        },
      },
    };

    const artificialTables = generateArtificialTables(newLookupTables);
    dispatch(resetArtificialFeatures(artificialTables.visible));

    return {
      selectedArtificialPostcode: artificialTables.full.POSTCODE.find(
        (artificialPostcode) => artificialPostcode.postcode === postcode
      ),
      lookupTables: newLookupTables,
      artificialTables,
    };
  }
);

export const deleteArtificialPostcode = createAsyncThunk(
  "depot/deleteArtificialPostcode",
  async (params, { getState, dispatch }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const artificialPostcodeEdit = artificialPostcodeInEditSelector(state);
    const { postcode } = artificialPostcodeEdit;
    const sector = Postcode.toSector(postcode);
    const lookupTables = lookupTableSelector(state);
    const postcodesInSector = lookupTables.SECTOR[sector].children;

    if (postcodesInSector < 2) {
      dispatch(
        addNotification({
          message: ERROR_REMOVE_LAST_POSTCODE(sector),
          options: { variant: "error" },
        })
      );
      throw new Error(ERROR_REMOVE_LAST_POSTCODE(sector));
    }

    const {
      data: { deliveryPlans, collectionPlans, overridePlans },
    } = await api.get(
      IS_POSTCODE_IN_USE(depotCode, postcode, postcodesInSector)
    );

    if (
      deliveryPlans.length ||
      collectionPlans.length ||
      overridePlans.length
    ) {
      dispatch(setArtificialPostcodeEdit({ artificialPostcode: null }));
      dispatch(
        setPostcodeInUseModalContent({
          postcode,
          deliveryPlans,
          collectionPlans,
          overridePlans,
        })
      );

      throw new Error(ERROR_REMOVE_IN_USE_POSTCODE(postcode));
    }

    const newLookupTables = {
      ...lookupTables,
      SECTOR: {
        ...lookupTables.SECTOR,
        [sector]: {
          ...lookupTables.SECTOR[sector],
          children: lookupTables.SECTOR[sector].children - 1,
        },
      },
      POSTCODE: { ...lookupTables.POSTCODE },
    };

    delete newLookupTables.POSTCODE[postcode];
    const artificialTables = generateArtificialTables(newLookupTables);
    dispatch(resetArtificialFeatures(artificialTables.visible));
    return {
      lookupTables: newLookupTables,
      artificialTables,
    };
  }
);

export const deleteArtificialPostcodeLocation = createAsyncThunk(
  "depot/deleteArtificialPostcodeLocation",
  async (params, { getState, dispatch }) => {
    const state = getState();
    const artificialPostcodeEdit = artificialPostcodeInEditSelector(state);
    const lookupTables = lookupTableSelector(state);
    const { postcode, level } = artificialPostcodeEdit;
    const newArtificialPostcodeEdit = { ...artificialPostcodeEdit };

    const newLookupTables = {
      ...lookupTables,
      [level]: cloneDeep(lookupTables[level]),
    };
    newLookupTables[level][postcode].center = null;
    newArtificialPostcodeEdit.lng = null;
    newArtificialPostcodeEdit.lat = null;

    const artificialTables = generateArtificialTables(newLookupTables);
    dispatch(resetArtificialFeatures(artificialTables.visible));

    return {
      artificialPostcodeEdit: newArtificialPostcodeEdit,
      lookupTables: newLookupTables,
      artificialTables,
    };
  }
);

export const setArtificialPostcodeEdit = createAsyncThunk(
  "depot/setArtificialPostcodeEdit",
  async ({ artificialPostcode }) => {
    return {
      artificialPostcodeEdit: artificialPostcode,
    };
  }
);

export const applyArtificialPostcodeEdit = createAsyncThunk(
  "depot/applyArtificialPostcodeEdit",
  (params) => ({})
);

export const cancelArtificialPostcodeEdit = createAsyncThunk(
  "depot/cancelArtificialPostcodeEdit",
  async (params, { getState, dispatch }) => {
    const state = getState();
    const artificialPostcodeEditCopy =
      artificialPostcodeInEditCopySelector(state);
    const lookupTables = lookupTableSelector(state);
    const { postcode, level, lng, lat } = artificialPostcodeEditCopy;

    const restoredLookupTables = {
      ...lookupTables,
      [level]: cloneDeep(lookupTables[level]),
    };
    restoredLookupTables[level][postcode].center =
      !isNil(lat) && !isNil(lng) ? [lng, lat] : null;

    const artificialTables = generateArtificialTables(restoredLookupTables);
    dispatch(resetArtificialFeatures(artificialTables.visible));

    return {
      lookupTables: restoredLookupTables,
      artificialTables,
    };
  }
);
